import React from 'react';
import {
  TextInput,
  PasswordInput,
  MultiSelect,
  Switch,
  Stack,
  Group,
  Button,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import axios from 'axios';
import { User, UserRole } from '../../../types';

interface UserFormProps {
  user: User | null;
  isEditing: boolean;
  token: string;
  onSuccess: () => void;
}

export const UserForm: React.FC<UserFormProps> = ({ user, isEditing, token, onSuccess }) => {
  const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';
  
  // Format phone number to international format
  const formatPhoneNumber = (phone: string) => {
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    
    // If it starts with 0, replace with +91 (India)
    if (cleaned.startsWith('0')) {
      return `+91${cleaned.substring(1)}`;
    }
    
    // If it's 10 digits, add +91
    if (cleaned.length === 10) {
      return `+91${cleaned}`;
    }
    
    // If it already has +, keep as is
    if (phone.startsWith('+')) {
      return phone;
    }
    
    return phone;
  };

  // Role options based on backend roles
  const roleOptions = [
    { value: UserRole.ADMIN, label: 'Admin', color: 'red', description: 'Full system access' },
    { value: UserRole.DIRECTOR, label: 'Director', color: 'purple', description: 'Director level access' },
    { value: UserRole.MANAGER, label: 'Manager', color: 'blue', description: 'Manager level access' },
    { value: UserRole.USER, label: 'User', color: 'green', description: 'Basic user access' },
  ];

  // Get default roles based on user or fallback to USER
  const getDefaultRoles = () => {
    if (user?.roles && user.roles.length > 0) {
      return user.roles;
    }
    return [UserRole.USER]; // Default to USER role
  };

  const form = useForm({
    initialValues: {
      username: user?.username || '',
      email: user?.email || '',
      phoneNumber: user?.phoneNumber || '',
      fullName: user?.fullName || '',
      password: '',
      roles: getDefaultRoles(),
      enabled: user?.enabled ?? true,
      twoFactorEnabled: user?.twoFactorEnabled ?? false,
    },
    validate: {
      username: (value) => {
        if (!value) return 'Username is required';
        if (value.length < 3) return 'Username must be at least 3 characters';
        if (!/^[a-zA-Z0-9._-]+$/.test(value)) {
          return 'Username can only contain letters, numbers, dots, underscores, and hyphens';
        }
        return null;
      },
      email: (value) => {
        if (!value) return 'Email is required';
        if (!/^\S+@\S+\.\S+$/.test(value)) return 'Invalid email format';
        return null;
      },
      phoneNumber: (value) => {
        if (!value) return 'Phone number is required';
        const cleaned = value.replace(/\D/g, '');
        if (cleaned.length < 10) return 'Phone number must be at least 10 digits';
        if (cleaned.length > 15) return 'Phone number is too long';
        return null;
      },
      fullName: (value) => {
        if (!value) return 'Full name is required';
        if (value.length < 2) return 'Full name is too short';
        return null;
      },
      ...(!isEditing && {
        password: (value) => {
          if (!value) return 'Password is required';
          if (value.length < 6) return 'Password must be at least 6 characters';
          if (!/(?=.*[A-Z])/.test(value)) return 'Password must contain at least one uppercase letter';
          if (!/(?=.*[a-z])/.test(value)) return 'Password must contain at least one lowercase letter';
          if (!/(?=.*\d)/.test(value)) return 'Password must contain at least one number';
          return null;
        },
      }),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    console.log('🔍 Submitting user with 2FA:', values.twoFactorEnabled);
    console.log('📦 Full payload:', values);
    
    try {
      // Format phone number before sending
      const formattedValues = {
        ...values,
        phoneNumber: formatPhoneNumber(values.phoneNumber)
      };
      
      console.log('📤 Sending formatted data:', formattedValues);
      
      if (isEditing && user) {
        await axios.put(`${API_URL}/users/${user.id}`, formattedValues, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        notifications.show({
          title: 'Success',
          message: 'User updated successfully',
          color: 'green',
        });
      } else {
        await axios.post(`${API_URL}/users`, formattedValues, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        notifications.show({
          title: 'Success',
          message: 'User created successfully',
          color: 'green',
        });
      }
      onSuccess();
    } catch (error: any) {
      console.error('❌ Error saving user:', error);
      console.error('📄 Error response:', error.response?.data);
      
      // Show specific error message
      if (error.response?.data?.message) {
        notifications.show({
          title: 'Error',
          message: error.response.data.message,
          color: 'red',
        });
      } else if (error.response?.data?.errors) {
        // Handle validation errors
        const validationErrors = error.response.data.errors;
        Object.keys(validationErrors).forEach(field => {
          form.setFieldError(field, validationErrors[field]);
        });
        notifications.show({
          title: 'Validation Error',
          message: 'Please check the form for errors',
          color: 'yellow',
        });
      } else {
        notifications.show({
          title: 'Error',
          message: 'Failed to save user',
          color: 'red',
        });
      }
    }
  };

  // Get role badge color for display
  const getRoleColor = (role: string) => {
    const option = roleOptions.find(opt => opt.value === role);
    return option?.color || 'gray';
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <TextInput
          label="Username"
          placeholder="Enter username (letters, numbers, dots, underscores, hyphens)"
          {...form.getInputProps('username')}
          required
          disabled={isEditing}
          description="Username must be 3-20 characters and unique"
        />
        
        <TextInput
          label="Email"
          placeholder="Enter email"
          {...form.getInputProps('email')}
          required
          description="Will be used for login and notifications"
        />
        
        <TextInput
          label="Phone Number"
          placeholder="Enter phone number (10 digits)"
          description="Will be formatted to international format (e.g., +919390038437)"
          {...form.getInputProps('phoneNumber')}
          required
        />
        
        <TextInput
          label="Full Name"
          placeholder="Enter full name"
          {...form.getInputProps('fullName')}
          required
        />
        
        {!isEditing && (
          <PasswordInput
            label="Password"
            placeholder="Enter password (min 6 characters, uppercase, lowercase, number)"
            {...form.getInputProps('password')}
            required
            description="Must contain at least one uppercase, one lowercase, and one number"
          />
        )}
        
        <MultiSelect
          label="Roles"
          placeholder="Select roles"
          data={roleOptions.map(role => ({
            value: role.value,
            label: `${role.label} - ${role.description}`
          }))}
          {...form.getInputProps('roles')}
          required
          description="Select the permissions level for this user"
          renderOption={({ option }) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div 
                style={{ 
                  width: '10px', 
                  height: '10px', 
                  borderRadius: '50%', 
                  backgroundColor: getRoleColor(option.value),
                  display: 'inline-block'
                }} 
              />
              <span>{option.label}</span>
            </div>
          )}
        />
        
        <Switch
          label="Account Enabled"
          description="If disabled, user cannot log in"
          {...form.getInputProps('enabled', { type: 'checkbox' })}
        />
        
        <Switch
          label="Enable Two-Factor Authentication"
          description="Adds an extra layer of security"
          {...form.getInputProps('twoFactorEnabled', { type: 'checkbox' })}
        />
        
        <Group justify="flex-end" mt="md">
          <Button type="submit" color="exportGreen" size="md">
            {isEditing ? 'Update User' : 'Create User'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};